import React, { ReactNode } from 'react';
import { Skeleton } from '../Skeleton/Skeleton';
import './DataTable.css';

export interface ColumnDef<T> {
  id: string;
  header: string;
  render?: (row: T) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  title?: ReactNode;
  subtitle?: string;
  showPagination?: boolean;
  totalRecords?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  onRowClick?: (row: T) => void;
  selectedRowId?: string | null;
  getRowId: (row: T) => string;
  emptyText?: string;
  emptyIcon?: ReactNode;
  actionsColumn?: (row: T) => ReactNode;
  actionsHeader?: string;
  className?: string;
  isLoading?: boolean;
}

export function DataTable<T>({
  data,
  columns,
  title,
  subtitle,
  showPagination,
  totalRecords = 0,
  currentPage = 1,
  onPageChange,
  onRowClick,
  selectedRowId,
  getRowId,
  emptyText = 'No_se_encontraron_registros',
  emptyIcon,
  actionsColumn,
  actionsHeader = 'Acciones',
  className = '',
  isLoading = false
}: DataTableProps<T>) {
  return (
    <div className={`data-table-container ${className}`}>
      {title && (
        <div className="data-table-header">
          <h3 className="data-table-title">{title}</h3>
          {subtitle && <span className="data-table-subtitle">{subtitle}</span>}
        </div>
      )}

      <div className="data-table-wrapper">
        <table className="data-table">
          <thead className="data-table-thead">
            <tr>
              {columns.map(col => (
                <th 
                  key={col.id} 
                  className={`data-table-th ${col.align === 'right' ? 'right' : ''} ${col.align === 'center' ? 'center' : ''}`}
                >
                  {col.header}
                </th>
              ))}
              {actionsColumn && <th className="data-table-th right">{actionsHeader}</th>}
            </tr>
          </thead>
          <tbody className="data-table-tbody">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`} className="data-table-row" data-keep-detail>
                  {columns.map(col => (
                    <td 
                      key={`skeleton-${rowIndex}-${col.id}`} 
                      className={`data-table-td ${col.className || ''} ${col.align === 'right' ? 'right' : ''} ${col.align === 'center' ? 'center' : ''}`}
                    >
                      <Skeleton />
                    </td>
                  ))}
                  {actionsColumn && (
                    <td className="data-table-td right data-table-actions">
                      <Skeleton />
                    </td>
                  )}
                </tr>
              ))
            ) : (
              data.map((row) => {
                const rowId = getRowId(row);
                const isSelected = selectedRowId === rowId;
                
                return (
                  <tr
                    key={rowId}
                    onClick={() => onRowClick && onRowClick(row)}
                    className={`data-table-row ${isSelected ? 'selected' : ''} ${onRowClick ? 'clickable' : ''}`}
                    data-keep-detail
                  >
                    {columns.map(col => (
                      <td 
                        key={col.id} 
                        className={`data-table-td ${col.className || ''} ${col.align === 'right' ? 'right' : ''} ${col.align === 'center' ? 'center' : ''}`}
                      >
                        {col.render ? col.render(row) : (row as any)[col.id]}
                      </td>
                    ))}
                    {actionsColumn && (
                      <td 
                        className="data-table-td right data-table-actions"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {actionsColumn(row)}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!isLoading && data.length === 0 && emptyText && (
        <div className="data-table-empty-state">
          {emptyIcon && <div className="data-table-empty-icon">{emptyIcon}</div>}
          <span className="data-table-empty-text">{emptyText}</span>
        </div>
      )}

      {showPagination && (
        <div className="data-table-pagination">
          <div className="data-table-pagination-info">
            Mostrando <span>{isLoading ? 0 : data.length}</span> de <span>{totalRecords}</span> registros
          </div>
          <div className="data-table-pagination-controls">
            <button 
              className="data-table-pagination-btn" 
              disabled={currentPage <= 1 || isLoading}
              onClick={() => onPageChange?.(currentPage - 1)}
            >
              PREV
            </button>
            <button className="data-table-pagination-btn active">{currentPage}</button>
            <button 
              className="data-table-pagination-btn"
              disabled={currentPage * 50 >= totalRecords || isLoading}
              onClick={() => onPageChange?.(currentPage + 1)}
            >
              NEXT
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

